import { useState } from 'react';
import { ChevronLeft, ChevronDown, Package, Star } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { useSocial } from '../hooks/useSocial';
import { getVariantConfig } from '../utils/cardVariants';
import { formatCardNumber, getMergedSectionLabel } from '../utils/setMerges';
import FilterBar from './FilterBar';
import PackEnergyTracker from './PackEnergyTracker';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI4MCIgdmlld0JveD0iMCAwIDIwMCAyODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjEwMCIgY3k9IjE0MCIgcj0iNDAiIGZpbGw9IiNEMUQ1REIiLz4KPC9zdmc+';

const CardGrid = ({ onBack, onCardSelect }) => {
  const {
    cards,
    currentSet,
    loading,
    getTotalQuantity,
    getVariantQuantity,
    incrementCard,
    decrementCard,
    calculateCompletion,
    settings,
    getFilteredCards,
    getSetPackCount,
    getSetTrackedHitRate
  } = useCollection();

  const { isCardWishlisted } = useSocial();

  const [trackerOpen, setTrackerOpen] = useState(false);

  const displayCards = getFilteredCards();

  // Merged child sets (BUG-010 — 30th Celebration's Classic Collection) get
  // their own section after the parent's cards. Cards arrive sorted parent-
  // first, so each section is one contiguous run.
  const sections = [];
  displayCards.forEach((card) => {
    const label = getMergedSectionLabel(card.set?.id);
    const last = sections[sections.length - 1];
    if (last && last.label === label) last.cards.push(card);
    else sections.push({ label, cards: [card] });
  });

  const completion = calculateCompletion();
  const completionPercentage = completion.total > 0 ?
    Math.round((completion.collected / completion.total) * 100) : 0;

  const setPackCount = currentSet ? getSetPackCount(currentSet.id) : 0;
  const setHitRate = currentSet ? getSetTrackedHitRate(currentSet.id) : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy-dark border-t-transparent mb-4"></div>
        <span className="text-ink-secondary text-sm">Loading Pokemon cards...</span>
      </div>
    );
  }

  if (!currentSet) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center px-6">
        <p className="text-ink-secondary text-sm mb-4">No Pokemon set is currently loaded.</p>
        <button
          onClick={onBack}
          className="bg-navy-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy-light transition-colors"
        >
          ← Back to Sets
        </button>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center px-6">
        <p className="text-ink-secondary text-sm mb-4">No cards found for this set.</p>
        <button
          onClick={onBack}
          className="bg-navy-dark text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy-light transition-colors"
        >
          ← Back to Sets
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Sub-header */}
      <div className="bg-navy-dark border-b-[3px] border-yellow px-4 md:px-10 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-white/70 hover:text-white text-sm flex-shrink-0"
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <span className="text-white/30 hidden sm:inline">/</span>
            <h2 className="font-heading font-bold text-white text-base md:text-lg truncate">{currentSet.name}</h2>
          </div>
          <span className="text-[11px] font-medium text-white bg-white/10 px-2.5 py-1 rounded-full flex-shrink-0">
            {completion.collected}/{completion.total}
          </span>
        </div>

        <div className="mt-3">
          <div className="h-[3px] bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-yellow transition-[width] duration-400 ease-out"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[11px] text-white/50">
            <span>{completionPercentage}% {settings.completionView === 'base' ? 'base' : 'master'} set complete</span>
            <span>{setPackCount} packs • {setHitRate}% hit rate</span>
          </div>
        </div>
      </div>

      {/* Pack & energy tracking — log packs while browsing the set's cards */}
      <div className="bg-cream-card border-b border-border">
        <button
          onClick={() => setTrackerOpen((prev) => !prev)}
          className="w-full flex items-center justify-between px-4 md:px-10 py-2.5 text-[12px] font-medium text-ink-secondary hover:text-ink-primary transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <Package size={13} />
            Pack &amp; Energy Tracking
            <span className="text-ink-faint">
              — {setPackCount} pack{setPackCount === 1 ? '' : 's'} • {setHitRate}% hit rate
            </span>
          </span>
          <ChevronDown
            size={14}
            className={`transition-transform ${trackerOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {trackerOpen && (
          <div className="px-4 md:px-10 pb-4">
            <div className="max-w-sm">
              <PackEnergyTracker set={currentSet} energyDefaultExpanded />
            </div>
          </div>
        )}
      </div>

      <FilterBar />

      <div className="px-4 md:px-10 py-6">
        {displayCards.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-ink-secondary text-sm mb-1">No cards match your current filters.</p>
            <p className="text-ink-faint text-xs">Try adjusting your search or filter settings.</p>
          </div>
        ) : (
          sections.map((section, i) => (
            <div key={section.label || 'main'} className={section.label && i > 0 ? 'mt-8' : ''}>
              {section.label && (
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border">
                  <h3 className="font-heading font-bold text-sm text-ink-primary">{section.label}</h3>
                  <span className="text-[11px] text-ink-muted">{section.cards.length} cards</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {section.cards.map((card) => {
                  const totalOwned = getTotalQuantity(card.id);

                  return (
                    <div
                      key={card.id}
                      onClick={() => onCardSelect && onCardSelect(card)}
                      className={`bg-white rounded-xl border overflow-hidden cursor-pointer transition-shadow hover:shadow-card ${
                        totalOwned > 0 ? 'border-success-green/40' : 'border-border'
                      }`}
                    >
                      <div className="flex items-stretch">
                        <div className="flex-1 min-w-0 p-2.5 flex flex-col justify-center">
                          <div className="text-[9px] font-semibold text-ink-faint tracking-wide">
                            {formatCardNumber(card)}
                          </div>
                          <div className="text-[11px] font-bold text-ink-primary truncate mt-0.5" title={card.name}>
                            {card.name}
                          </div>
                          <div className="text-[10px] text-ink-secondary truncate mt-0.5">{card.rarity}</div>
                          {card.hp && (
                            <div className="text-[10px] text-brick font-semibold mt-0.5">HP {card.hp}</div>
                          )}
                          {card.types && (
                            <div className="text-[10px] text-ink-muted truncate mt-0.5">{card.types.join(', ')}</div>
                          )}
                        </div>

                        <div className="relative bg-gradient-to-br from-cream-card to-cream-bg w-[92px] md:w-[110px] h-[120px] md:h-[140px] flex-shrink-0 flex items-center justify-center p-2">
                          <img
                            src={card.images.small}
                            alt={card.name}
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.src = FALLBACK_IMG;
                            }}
                          />
                          {totalOwned > 0 && (
                            <span className="absolute top-1.5 right-1.5 w-3 h-3 rounded-full bg-success-green border-2 border-white" />
                          )}
                          {isCardWishlisted(card.id) && (
                            <span className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full bg-yellow border border-white flex items-center justify-center">
                              <Star size={9} className="text-navy-dark" fill="currentColor" />
                            </span>
                          )}
                        </div>
                      </div>

                      {card.variants?.length > 0 && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex flex-col gap-1.5 px-2.5 py-2 border-t border-border bg-cream-bg/50"
                        >
                          {card.variants.map((variant) => {
                            const qty = getVariantQuantity(card.id, variant);
                            const config = getVariantConfig(variant);

                            return (
                              <div key={variant} className="flex items-center justify-between gap-2">
                                <span
                                  className={`${config.color} text-white text-[9px] font-bold rounded px-1 py-0.5 min-w-[24px] text-center`}
                                >
                                  {config.short}
                                </span>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => decrementCard(card.id, variant)}
                                    disabled={qty === 0}
                                    className={`w-7 h-7 md:w-[22px] md:h-[22px] rounded-full text-white text-[10px] flex items-center justify-center ${
                                      qty === 0 ? 'bg-border cursor-not-allowed' : 'bg-brick hover:opacity-80'
                                    }`}
                                  >
                                    −
                                  </button>
                                  <span className="w-5 text-center text-[10px] font-bold text-ink-primary">{qty}</span>
                                  <button
                                    onClick={() => incrementCard(card.id, variant)}
                                    className="w-7 h-7 md:w-[22px] md:h-[22px] rounded-full bg-success-green text-white text-[10px] flex items-center justify-center hover:opacity-80"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="bg-white border-t border-border px-4 md:px-10 py-4 text-center text-sm text-ink-secondary">
        {displayCards.length === cards.length ? (
          <span>
            Displaying all <span className="font-semibold text-ink-primary">{cards.length}</span> cards from{' '}
            <span className="font-semibold text-ink-primary">{currentSet.name}</span>
          </span>
        ) : (
          <span>
            Showing <span className="font-semibold text-ink-primary">{displayCards.length}</span> of{' '}
            <span className="font-semibold text-ink-primary">{cards.length}</span> cards
          </span>
        )}
      </div>
    </div>
  );
};

export default CardGrid;
